/**
 * AdminUniversalScanner — single page with two tabs:
 *   • POS Scanner: scan product QRs to build a cart with totals.
 *   • Asset Scanner: scan asset QRs to toggle check-in/out + log history.
 *
 * Designed for a USB QR reader (keyboard-emulation): the scan input
 * auto-focuses and submits on Enter. Works equally with manual paste.
 *
 * Route: /admin/qr-scanner
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  ScanLine, ShoppingCart, Boxes, Trash2, X, Plus, Minus,
  ArrowRightLeft, CheckCircle2, Activity, Hotel, AlertTriangle, XCircle,
  IdCard,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

// ---------------------------------------------------------------------------
// POS Tab
// ---------------------------------------------------------------------------
const PosTab = () => {
  const [qr, setQr] = useState('');
  const [cart, setCart] = useState([]); // [{id,name,sku,price,currency,qty}]
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (raw) => {
    const value = (raw ?? qr).trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/pos-qr/scan`, { qr: value },
                                       { headers: authH() });
      if (!res.data.ok) {
        toast.error(res.data.reason || 'Scan failed');
      } else {
        const p = res.data.product;
        setCart(prev => {
          const idx = prev.findIndex(x => x.id === p.id);
          if (idx >= 0) {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
            return copy;
          }
          return [...prev, { ...p, qty: 1 }];
        });
        toast.success(`Added ${p.name}`);
      }
      setQr('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Scan failed');
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const subtotal = useMemo(
    () => cart.reduce((s, x) => s + (Number(x.price) || 0) * x.qty, 0),
    [cart]
  );
  const currency = cart[0]?.currency || 'USD';

  const updateQty = (id, delta) => setCart(prev => {
    const out = prev.map(x => x.id === id ? { ...x, qty: x.qty + delta } : x);
    return out.filter(x => x.qty > 0);
  });
  const removeItem = (id) => setCart(prev => prev.filter(x => x.id !== id));
  const clear = () => setCart([]);

  return (
    <div className="grid lg:grid-cols-5 gap-4" data-testid="pos-scanner-tab">
      {/* Scanner input */}
      <Card className="lg:col-span-3 bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm text-slate-200 flex items-center gap-2">
            <ScanLine className="h-4 w-4" /> Scan a product QR or SKU
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input ref={inputRef} value={qr}
                   onChange={e => setQr(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), submit())}
                   placeholder="Scan or paste here (FWM:v1:prod:… or plain SKU); press Enter"
                   className="bg-slate-900 border-slate-800 font-mono text-sm"
                   data-testid="pos-scan-input" />
            <Button onClick={() => submit()} disabled={busy || !qr.trim()}
                     data-testid="pos-scan-btn">
              {busy ? 'Scanning…' : 'Add'}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">
            Tip: any USB QR scanner that emulates keyboard input will work — focus the field, scan, the cart updates automatically.
          </p>
        </CardContent>
      </Card>

      {/* Cart */}
      <Card className="lg:col-span-2 bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm text-slate-200 flex items-center justify-between">
            <span className="inline-flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" /> Cart ({cart.length})
            </span>
            {cart.length > 0 && (
              <Button size="sm" variant="ghost" onClick={clear}
                      data-testid="pos-clear-btn">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 max-h-[480px] overflow-y-auto">
          {cart.length === 0 && (
            <p className="text-xs text-slate-500">Scan a product to start building a cart.</p>
          )}
          {cart.map(x => (
            <div key={x.id} className="flex items-center gap-2 border border-slate-800 rounded-md p-2 bg-slate-800/30"
                 data-testid={`cart-row-${x.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-200 truncate">{x.name}</div>
                <div className="text-[10px] text-slate-500">{x.sku} · {currency} {Number(x.price || 0).toFixed(2)}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => updateQty(x.id, -1)}
                       data-testid={`cart-minus-${x.id}`}>
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <span className="text-sm tabular-nums w-6 text-center">{x.qty}</span>
              <Button size="sm" variant="ghost" onClick={() => updateQty(x.id, +1)}
                       data-testid={`cart-plus-${x.id}`}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => removeItem(x.id)}>
                <X className="h-3.5 w-3.5 text-rose-300" />
              </Button>
            </div>
          ))}
        </CardContent>
        {cart.length > 0 && (
          <div className="border-t border-slate-800 p-4 flex items-center justify-between"
               data-testid="cart-total-row">
            <span className="text-slate-400 text-sm">Subtotal</span>
            <span className="text-2xl font-semibold text-emerald-300 tabular-nums">
              {currency} {subtotal.toFixed(2)}
            </span>
          </div>
        )}
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Asset Tab
// ---------------------------------------------------------------------------
const AssetTab = () => {
  const qc = useQueryClient();
  const [qr, setQr] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [lastResult]);

  const { data: recent = [] } = useQuery({
    queryKey: ['asset-recent'],
    queryFn: async () => (
      await axios.get(`${API}/api/assets-qr/recent-scans?limit=20`,
                       { headers: authH() })
    ).data,
    refetchInterval: 8000,
  });

  const submit = async (raw) => {
    const value = (raw ?? qr).trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/assets-qr/scan`, {
        qr: value,
        assigned_to: assignedTo || null,
        location: location || null,
        note: note || null,
      }, { headers: authH() });
      setLastResult(res.data);
      if (!res.data.ok) {
        toast.error(res.data.reason || 'Scan failed');
      } else {
        toast.success(
          res.data.new_status === 'checked_out'
            ? `Checked out: ${res.data.asset?.name}`
            : `Checked in: ${res.data.asset?.name}`
        );
        qc.invalidateQueries({ queryKey: ['asset-recent'] });
      }
      setQr('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Scan failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="asset-scanner-tab">
      <Card className="lg:col-span-2 bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm text-slate-200 flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" /> Scan an asset QR to check in/out
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-2">
            <Input value={assignedTo} onChange={e => setAssignedTo(e.target.value)}
                   placeholder="Assigned to (user id, optional)"
                   className="bg-slate-900 border-slate-800"
                   data-testid="asset-assigned-input" />
            <Input value={location} onChange={e => setLocation(e.target.value)}
                   placeholder="New location (optional)"
                   className="bg-slate-900 border-slate-800"
                   data-testid="asset-location-input" />
          </div>
          <Input value={note} onChange={e => setNote(e.target.value)}
                 placeholder="Note (optional)"
                 className="bg-slate-900 border-slate-800"
                 data-testid="asset-note-input" />
          <div className="flex gap-2">
            <Input ref={inputRef} value={qr}
                   onChange={e => setQr(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), submit())}
                   placeholder="Scan/paste asset QR (FWM:v1:asst:…) or asset tag; press Enter"
                   className="bg-slate-900 border-slate-800 font-mono text-sm"
                   data-testid="asset-scan-input" />
            <Button onClick={() => submit()} disabled={busy || !qr.trim()}
                     data-testid="asset-scan-btn">
              {busy ? 'Scanning…' : 'Toggle'}
            </Button>
          </div>

          {lastResult && lastResult.ok && (
            <div className="rounded-xl ring-2 ring-emerald-400/60 bg-emerald-500/10 p-4 text-emerald-100"
                 data-testid="asset-result">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-8 w-8 shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-wider opacity-80">
                    {lastResult.previous_status} → {lastResult.new_status}
                  </div>
                  <div className="text-xl font-semibold">{lastResult.asset?.name}</div>
                  <div className="text-xs opacity-80 mt-1 flex gap-2 flex-wrap">
                    {lastResult.asset?.asset_tag && (
                      <span>Tag: <strong>{lastResult.asset.asset_tag}</strong></span>
                    )}
                    {lastResult.asset?.location && (
                      <span>Location: <strong>{lastResult.asset.location}</strong></span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {lastResult && !lastResult.ok && (
            <div className="rounded-xl ring-2 ring-rose-400/60 bg-rose-500/10 p-4 text-rose-100">
              {lastResult.reason}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm text-slate-200 flex items-center gap-2">
            <Activity className="h-4 w-4" /> Recent scans
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 max-h-[480px] overflow-y-auto"
                      data-testid="asset-recent-list">
          {recent.length === 0 && (
            <p className="text-xs text-slate-500">No scans yet.</p>
          )}
          {recent.map(r => {
            const style = r.action === 'checked_in'
              ? 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10'
              : r.action === 'checked_out'
                ? 'border-amber-400/40 text-amber-300 bg-amber-500/10'
                : r.action?.startsWith('set_maintenance')
                  ? 'border-violet-400/40 text-violet-300 bg-violet-500/10'
                  : 'border-slate-500/40 text-slate-300 bg-slate-500/10';
            return (
              <div key={r.id} className="border border-slate-800 rounded-md p-2 bg-slate-800/30 text-xs">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-slate-200 font-medium truncate">{r.asset_name || '—'}</span>
                  <Badge variant="outline" className={`text-[10px] ${style}`}>
                    {r.action}
                  </Badge>
                </div>
                <div className="text-slate-500 mt-0.5">
                  {r.asset_tag} · {r.by_name || 'admin'} · {r.scanned_at ? new Date(r.scanned_at).toLocaleString() : ''}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Housing Tab
// ---------------------------------------------------------------------------
const HOUSING_STYLES = {
  green:  { icon: CheckCircle2, ring: 'ring-emerald-400/60', bg: 'bg-emerald-500/15', text: 'text-emerald-200' },
  amber:  { icon: AlertTriangle, ring: 'ring-amber-400/60', bg: 'bg-amber-500/15', text: 'text-amber-200' },
  red:    { icon: XCircle, ring: 'ring-rose-400/60', bg: 'bg-rose-500/15', text: 'text-rose-200' },
};

const HousingTab = () => {
  const qc = useQueryClient();
  const [qr, setQr] = useState('');
  const [accessPoint, setAccessPoint] = useState('main_gate');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [lastResult]);

  const { data: recent = [] } = useQuery({
    queryKey: ['housing-recent'],
    queryFn: async () => (
      await axios.get(`${API}/api/housing-qr/recent-access?limit=20`,
                       { headers: authH() })
    ).data,
    refetchInterval: 8000,
  });

  const submit = async () => {
    const value = qr.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/housing-qr/scan`,
        { qr: value, access_point: accessPoint }, { headers: authH() });
      setLastResult(res.data);
      const v = res.data.verdict;
      if (v === 'green') toast.success(`Welcome ${res.data.guest?.name || ''}`);
      else if (v === 'amber') toast.warning(res.data.reason || 'Outside window');
      else toast.error(res.data.reason || 'Denied');
      qc.invalidateQueries({ queryKey: ['housing-recent'] });
      setQr('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Scan failed');
    } finally { setBusy(false); }
  };

  const Style = lastResult?.verdict ? HOUSING_STYLES[lastResult.verdict] : null;

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="housing-scanner-tab">
      <Card className="lg:col-span-2 bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm text-slate-200 flex items-center gap-2">
            <Hotel className="h-4 w-4" /> Housing pass scan (gate / room door)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={accessPoint} onChange={e => setAccessPoint(e.target.value)}
                   placeholder="Access point (main_gate, lobby, room_door)"
                   className="bg-slate-900 border-slate-800 w-64"
                   data-testid="housing-access-point" />
            <Input ref={inputRef} value={qr}
                   onChange={e => setQr(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), submit())}
                   placeholder="Scan/paste accommodation_pass QR or booking reference"
                   className="bg-slate-900 border-slate-800 font-mono text-sm"
                   data-testid="housing-scan-input" />
            <Button onClick={submit} disabled={busy || !qr.trim()}
                     data-testid="housing-scan-btn">
              {busy ? 'Scanning…' : 'Scan'}
            </Button>
          </div>

          {Style && (
            <div className={`rounded-xl ring-2 ${Style.ring} ${Style.bg} p-5 ${Style.text}`}
                 data-testid="housing-result">
              <div className="flex items-start gap-3">
                <Style.icon className="h-10 w-10 shrink-0" />
                <div className="flex-1">
                  <div className="text-lg font-semibold">{lastResult.reason}</div>
                  {lastResult.guest && (
                    <div className="text-2xl font-medium mt-1">{lastResult.guest.name}</div>
                  )}
                  <div className="text-xs opacity-80 mt-2 flex gap-3 flex-wrap">
                    {lastResult.room?.room_number && (
                      <span>Room: <strong>{lastResult.room.room_number}</strong></span>
                    )}
                    {lastResult.booking?.check_in_date && (
                      <span>Check-in: <strong>{lastResult.booking.check_in_date}</strong></span>
                    )}
                    {lastResult.booking?.check_out_date && (
                      <span>Check-out: <strong>{lastResult.booking.check_out_date}</strong></span>
                    )}
                    {lastResult.access_point && (
                      <span>Gate: <strong>{lastResult.access_point}</strong></span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {!Style && (
            <div className="rounded-xl border border-dashed border-slate-800 p-6 text-center text-sm text-slate-500">
              Awaiting first scan. GREEN = welcome · AMBER = outside check-in window · RED = cancelled / invalid.
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm text-slate-200 flex items-center gap-2">
            <Activity className="h-4 w-4" /> Recent gate scans
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 max-h-[480px] overflow-y-auto"
                      data-testid="housing-recent-list">
          {recent.length === 0 && (
            <p className="text-xs text-slate-500">No scans yet.</p>
          )}
          {recent.map(r => (
            <div key={r.id} className="border border-slate-800 rounded-md p-2 bg-slate-800/30 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-slate-200 font-medium truncate">{r.guest_name || '—'}</span>
                <Badge variant="outline" className={`text-[10px] ${
                  r.verdict === 'green' ? 'border-emerald-400/40 text-emerald-300'
                    : r.verdict === 'amber' ? 'border-amber-400/40 text-amber-300'
                      : 'border-rose-400/40 text-rose-300'
                }`}>{r.verdict}</Badge>
              </div>
              <div className="text-slate-500 mt-0.5">
                Room {r.room_number || '—'} · {r.access_point} · {r.scanned_at ? new Date(r.scanned_at).toLocaleString() : ''}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Universal Passes Tab — handles staff_id, meal_pass, visitor_pass,
// vehicle_tag, gate_pass, receipt_code, member_id via /api/passes/scan.
// ---------------------------------------------------------------------------
const PASS_STYLES = {
  green: { icon: CheckCircle2, ring: 'ring-emerald-400/60', bg: 'bg-emerald-500/15', text: 'text-emerald-200' },
  amber: { icon: AlertTriangle, ring: 'ring-amber-400/60', bg: 'bg-amber-500/15', text: 'text-amber-200' },
  red:   { icon: XCircle, ring: 'ring-rose-400/60', bg: 'bg-rose-500/15', text: 'text-rose-200' },
};

const PassesTab = () => {
  const qc = useQueryClient();
  const [qr, setQr] = useState('');
  const [scanPoint, setScanPoint] = useState('main');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [lastResult]);

  const { data: registry = [] } = useQuery({
    queryKey: ['passes-registry'],
    queryFn: async () => (
      await axios.get(`${API}/api/passes/registry`, { headers: authH() })
    ).data,
  });

  const { data: recent = [] } = useQuery({
    queryKey: ['passes-recent'],
    queryFn: async () => (
      await axios.get(`${API}/api/passes/recent?limit=20`, { headers: authH() })
    ).data,
    refetchInterval: 8000,
  });

  const submit = async () => {
    const value = qr.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/passes/scan`,
        { qr: value, scan_point: scanPoint }, { headers: authH() });
      setLastResult(res.data);
      const v = res.data.verdict;
      if (v === 'green') toast.success(`${res.data.token_type}: ${res.data.snapshot?.name || 'OK'}`);
      else if (v === 'amber') toast.warning(res.data.reason);
      else toast.error(res.data.reason);
      qc.invalidateQueries({ queryKey: ['passes-recent'] });
      setQr('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Scan failed');
    } finally { setBusy(false); }
  };

  const Style = lastResult?.verdict ? PASS_STYLES[lastResult.verdict] : null;
  const snap = lastResult?.snapshot || {};

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="passes-scanner-tab">
      <Card className="lg:col-span-2 bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm text-slate-200 flex items-center gap-2">
            <IdCard className="h-4 w-4" /> Universal Passes
            <span className="text-[10px] text-slate-500 font-normal ml-2">
              staff · meal · visitor · vehicle · gate · receipt · member
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={scanPoint} onChange={e => setScanPoint(e.target.value)}
                   placeholder="Scan point (main, gate, dining_hall…)"
                   className="bg-slate-900 border-slate-800 w-56"
                   data-testid="passes-scan-point" />
            <Input ref={inputRef} value={qr}
                   onChange={e => setQr(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), submit())}
                   placeholder="Scan/paste any FWM Universal QR"
                   className="bg-slate-900 border-slate-800 font-mono text-sm"
                   data-testid="passes-scan-input" />
            <Button onClick={submit} disabled={busy || !qr.trim()}
                     data-testid="passes-scan-btn">
              {busy ? 'Scanning…' : 'Scan'}
            </Button>
          </div>

          {Style && (
            <div className={`rounded-xl ring-2 ${Style.ring} ${Style.bg} p-5 ${Style.text}`}
                 data-testid="passes-result">
              <div className="flex items-start gap-3">
                <Style.icon className="h-10 w-10 shrink-0" />
                <div className="flex-1">
                  <div className="text-lg font-semibold">{lastResult.reason}</div>
                  <div className="text-2xl font-medium mt-1">
                    {snap.name || snap.plate || snap.receipt_number || '—'}
                  </div>
                  <div className="text-xs opacity-80 mt-2 flex gap-3 flex-wrap">
                    <Badge variant="outline" className="text-[10px] uppercase">{lastResult.token_type}</Badge>
                    {snap.role && <span>Role: <strong>{snap.role}</strong></span>}
                    {snap.tier_name && <span>Tier: <strong>{snap.tier_name}</strong></span>}
                    {snap.organisation && <span>Org: <strong>{snap.organisation}</strong></span>}
                    {snap.host_name && <span>Host: <strong>{snap.host_name}</strong></span>}
                    {snap.permission_level && <span>Permission: <strong>{snap.permission_level}</strong></span>}
                    {snap.total !== undefined && <span>Total: <strong>{snap.currency} {snap.total}</strong></span>}
                    {snap.membership_status && <span>Status: <strong>{snap.membership_status}</strong></span>}
                  </div>
                </div>
              </div>
            </div>
          )}
          {!Style && (
            <div className="rounded-xl border border-dashed border-slate-800 p-6 text-center text-sm text-slate-500">
              Awaiting first scan. {registry.length} pass types supported · auto-routes by QR type.
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm text-slate-200 flex items-center gap-2">
            <Activity className="h-4 w-4" /> Recent pass scans
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 max-h-[480px] overflow-y-auto"
                      data-testid="passes-recent-list">
          {recent.length === 0 && <p className="text-xs text-slate-500">No scans yet.</p>}
          {recent.map(r => (
            <div key={r.id} className="border border-slate-800 rounded-md p-2 bg-slate-800/30 text-xs">
              <div className="flex justify-between items-center gap-1">
                <span className="text-slate-200 font-medium truncate">{r.snapshot_name || '—'}</span>
                <Badge variant="outline" className={`text-[10px] ${
                  r.verdict === 'green' ? 'border-emerald-400/40 text-emerald-300'
                    : r.verdict === 'amber' ? 'border-amber-400/40 text-amber-300'
                      : 'border-rose-400/40 text-rose-300'
                }`}>{r.verdict}</Badge>
              </div>
              <div className="text-slate-500 mt-0.5">
                {r.token_type} · {r.scan_point} · {r.scanned_at ? new Date(r.scanned_at).toLocaleTimeString() : ''}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const AdminUniversalScanner = () => {
  const [tab, setTab] = useState('pos');
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-7xl mx-auto space-y-5">
        <header>
          <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
            <ScanLine className="h-6 w-6 text-indigo-400" /> Universal Scanner
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            One QR scanner page for POS and Asset tracking — every code maps back to the right module via the universal QR registry.
          </p>
        </header>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-900/60 border border-slate-800">
            <TabsTrigger value="pos" data-testid="tab-pos">
              <ShoppingCart className="h-4 w-4 mr-1" /> POS
            </TabsTrigger>
            <TabsTrigger value="assets" data-testid="tab-assets">
              <Boxes className="h-4 w-4 mr-1" /> Assets
            </TabsTrigger>
            <TabsTrigger value="housing" data-testid="tab-housing">
              <Hotel className="h-4 w-4 mr-1" /> Housing
            </TabsTrigger>
            <TabsTrigger value="passes" data-testid="tab-passes">
              <IdCard className="h-4 w-4 mr-1" /> Passes
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pos" className="mt-4"><PosTab /></TabsContent>
          <TabsContent value="assets" className="mt-4"><AssetTab /></TabsContent>
          <TabsContent value="housing" className="mt-4"><HousingTab /></TabsContent>
          <TabsContent value="passes" className="mt-4"><PassesTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminUniversalScanner;
